// Representative block averages from the eight user-provided 2026-10-06 strips,
// converted from their embedded Display ICC profile to sRGB. Keep this registry
// the single source of color values for the SDK, website and embedded runtime.
const definitions = [
    { id: 'cool', name: 'Cool', description: 'Teal, violet and blue palette.', colors: [[11, 130, 110], [52, 54, 97], [31, 107, 144], [2, 62, 115]] },
    { id: 'warm', name: 'Warm', description: 'Amber, peach and warm red palette.', colors: [[241, 179, 99], [242, 195, 156], [217, 156, 122], [200, 66, 39]] },
    { id: 'high-contrast', name: 'High Contrast', description: 'Dark anchors and bright whites with amber and navy.', colors: [[193, 106, 3], [0, 0, 0], [18, 40, 56], [253, 255, 254]] },
    { id: 'low-contrast', name: 'Low Contrast', description: 'Muted midtone earth colors and sage.', colors: [[160, 115, 84], [131, 110, 64], [157, 177, 165], [136, 123, 114]] },
    { id: 'high-saturation', name: 'High Saturation', description: 'Vivid yellow, orange, green and cyan.', colors: [[219, 184, 68], [240, 82, 25], [64, 181, 118], [14, 182, 195]] },
    { id: 'low-saturation', name: 'Low Saturation', description: 'Soft beige, pale sage and desaturated greens.', colors: [[182, 160, 136], [197, 219, 196], [132, 150, 124], [98, 115, 103]] },
    { id: 'film-inspired', name: 'Film-inspired', description: 'Warm earth, cream and deep red. A palette, not film emulation.', colors: [[156, 116, 78], [193, 156, 122], [139, 28, 18], [124, 76, 29]] },
    { id: 'black-and-white', name: 'Black & White', description: 'True monochrome: zero saturation, with linear-light luminance preserved.', colors: [[184, 184, 184], [120, 120, 120], [74, 74, 74], [5, 5, 5]] },
];
export const PCE_PALETTE_PRESETS = Object.freeze(definitions.map((preset) => Object.freeze({ ...preset, version: 1, colorSpace: 'sRGB',
    colors: Object.freeze(preset.colors.map((rgb) => Object.freeze([...rgb]))) })));
export function getPCEPalettePreset(id) {
    const preset = PCE_PALETTE_PRESETS.find((entry) => entry.id === id);
    if (!preset)
        throw new Error(`Unknown PCE palette preset: ${id}`);
    return preset;
}
export function pcePaletteReferenceMetadata(id) {
    const { id: presetId, name, version, colorSpace } = getPCEPalettePreset(id);
    return { id: presetId, name, version, colorSpace, type: 'palette-preset' };
}
/** Fresh buffer on every call; no DOM/canvas, models, image assets or network. */
export function createPCEPaletteFrame(id, dimensions = {}) {
    const preset = getPCEPalettePreset(id);
    const width = dimensions.width ?? 480, height = dimensions.height ?? 120;
    if (!Number.isInteger(width) || !Number.isInteger(height) || width < preset.colors.length || height < 1 || width > 720 || height > 720)
        throw new Error('PCE palette dimensions must be integer pixels: width 4–720 and height 1–720.');
    const data = new Uint8ClampedArray(width * height * 4);
    for (let y = 0; y < height; y++)
        for (let x = 0; x < width; x++) {
            const rgb = preset.colors[Math.min(preset.colors.length - 1, Math.floor(x * preset.colors.length / width))];
            const offset = (y * width + x) * 4;
            data[offset] = rgb[0];
            data[offset + 1] = rgb[1];
            data[offset + 2] = rgb[2];
            data[offset + 3] = 255;
        }
    return typeof ImageData !== 'undefined' ? new ImageData(data, width, height, { colorSpace: 'srgb' }) :
        { width, height, data, colorSpace: 'srgb' };
}
/** Use the regular PCE analyzer without supplying a reference image. */
export function createPCEPaletteInput(input) {
    return { sourceFrame: input.sourceFrame, referenceFrame: createPCEPaletteFrame(input.presetId),
        // The built-in palette has a known encoding, irrespective of source metadata.
        options: { ...input.options, palettePresetId: input.presetId, referenceInputColorSpace: 'sRGB', referenceInputTransferFunction: 'sRGB', referenceCameraProfile: undefined } };
}
//# sourceMappingURL=palettePresets.js.map